// Utility to build and download a CSV of the member list. The caller
// resolves display strings (localized role/status, plan names) and passes
// plain string rows so this module stays i18n-agnostic and easy to unit
// test.

// Fixed CSV headers. Plan columns (one per plan, header = plan name) are
// appended after the fixed columns. Unlike eAdmin, eCloud has no memo
// column.
export type MemberCsvHeaders = {
  name: string
  email: string
  // Organization role (localized: 組織管理者 / メンバー)
  organizationRole: string
  // Member status (localized: 有効 / 招待中 / 削除済み)
  status: string
  // Creation date (YYYY-MM-DD)
  createdAt: string
  phoneNumber: string
  mobileNumber: string
  // Dynamic plan column headers (plan names), in output order.
  planNames: string[]
}

// A single CSV row. Order of columns is fixed by the spec.
export type MemberCsvRow = {
  name: string
  email: string
  organizationRole: string
  status: string
  createdAt: string
  phoneNumber: string
  mobileNumber: string
  // Role names per plan column, aligned by index with headers.planNames.
  planRoles: string[]
}

// Neutralize spreadsheet formula injection: Excel evaluates cells starting
// with =, +, -, @, tab, or CR as formulas. Member names/emails are
// user-controlled, so prefix them with a single quote (OWASP recommendation).
function neutralizeFormulaInjection(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value
}

// Escape a single CSV field: neutralize formula injection, then apply
// RFC 4180 quoting (wrap in double quotes when the value contains a comma,
// double quote, or newline, and double any inner quotes).
export function escapeCsvField(value: string): string {
  const neutralized = neutralizeFormulaInjection(value)
  if (/[",\r\n]/.test(neutralized)) {
    return `"${neutralized.replace(/"/g, '""')}"`
  }
  return neutralized
}

// Like escapeCsvField, but always wraps the value in double quotes. Used for
// the member name column, which may contain commas.
// Pass { neutralize: false } to skip formula neutralization: phone numbers
// are format-validated upstream and may legitimately start with "+", where a
// leading single quote would visibly corrupt the number.
export function quoteCsvField(
  value: string,
  options?: { neutralize?: boolean },
): string {
  const neutralized =
    options?.neutralize === false ? value : neutralizeFormulaInjection(value)
  return `"${neutralized.replace(/"/g, '""')}"`
}

function toLine(fields: string[]): string {
  return fields.map(escapeCsvField).join(',')
}

// Build the CSV text (with a UTF-8 BOM so Excel opens it without garbling
// Japanese). The first line is the column headers and the rest are data rows.
export function buildMemberCsv(
  headers: MemberCsvHeaders,
  rows: MemberCsvRow[],
): string {
  const headerFields = [
    headers.name,
    headers.email,
    headers.organizationRole,
    headers.status,
    headers.createdAt,
    headers.phoneNumber,
    headers.mobileNumber,
    ...headers.planNames,
  ]

  const lines = [toLine(headerFields)]
  for (const row of rows) {
    const fields = [
      // The name is always quoted since it may contain commas.
      quoteCsvField(row.name),
      escapeCsvField(row.email),
      escapeCsvField(row.organizationRole),
      escapeCsvField(row.status),
      escapeCsvField(row.createdAt),
      // Phone numbers may start with "+" (international format); quote them
      // without neutralization so the visible value stays unchanged.
      quoteCsvField(row.phoneNumber, { neutralize: false }),
      quoteCsvField(row.mobileNumber, { neutralize: false }),
    ]
    // Keep the row aligned with the plan headers even if planRoles is short.
    for (let i = 0; i < headers.planNames.length; i++) {
      fields.push(escapeCsvField(row.planRoles[i] ?? ''))
    }
    lines.push(fields.join(','))
  }

  const BOM = '﻿'
  return BOM + lines.join('\r\n')
}

// Replace filesystem-reserved characters so an organization name embedded in
// the download filename cannot truncate or break the file name.
export function sanitizeFileName(name: string): string {
  return name.replace(/[\\/:*?"<>|]/g, '_')
}

// Trigger a browser download of the given CSV text.
export function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
